import React from "react";
import { RevenueVsExpensesCard } from "./RevenueVsExpensesCard";
import { CashInOutCard } from "./CashInOutCard";
import type { RevenueVsExpensesCardProps } from "./RevenueVsExpensesCard";
import type { CashInOutCardProps } from "./CashInOutCard";
import { useTwelveMonthTrend } from "../../hooks/useTwelveMonthTrend";
import { useTwelveMonthTrendInsights } from "../../hooks/useTwelveMonthTrendInsights";
import { TwelveMonthTrendSkeleton } from "../shared/TwelveMonthTrendSkeleton";

export interface TwelveMonthTrendSectionProps {
  sectionTitle?: string;
  revenueVsExpenses?: RevenueVsExpensesCardProps;
  cashInOut?: CashInOutCardProps;
}

export function TwelveMonthTrendSection(props?: TwelveMonthTrendSectionProps) {
  const result = useTwelveMonthTrend();
  const insightsData = useTwelveMonthTrendInsights({
    enabled: !result.loading && !result.error,
  });
  const opts = props ?? {};
  const revenueVsExpenses: RevenueVsExpensesCardProps =
    opts.revenueVsExpenses ??
    (insightsData.error
      ? {
          ...result.revenueVsExpenses,
          insightsLoading: false,
          insightsError: insightsData.error,
        }
      : {
          ...result.revenueVsExpenses,
          insights: {
            title: insightsData.revenueVsExpenses.title,
            body: insightsData.revenueVsExpenses.body,
            sentiment: insightsData.revenueVsExpenses.sentiment,
          },
          insightsLoading: insightsData.loading,
          insightsError: null,
        });
  const cashInOut: CashInOutCardProps =
    opts.cashInOut ??
    (insightsData.error
      ? {
          ...result.cashInOut,
          insightsLoading: false,
          insightsError: insightsData.error,
        }
      : {
          ...result.cashInOut,
          insights: {
            title: insightsData.cashInOut.title,
            body: insightsData.cashInOut.body,
            sentiment: insightsData.cashInOut.sentiment,
            lastClosedLabel: insightsData.cashInOut.lastClosed.monthLabel,
            inAmount: insightsData.cashInOut.lastClosed.cashIn,
            outAmount: insightsData.cashInOut.lastClosed.cashOut,
            netAmount: insightsData.cashInOut.lastClosed.net,
          },
          insightsLoading: insightsData.loading,
          insightsError: null,
        });

  if (result.loading) {
    return <TwelveMonthTrendSkeleton />;
  }

  return (
    <div className="flex w-full max-w-full min-w-0 flex-col items-start gap-4  bg-neutral-50 overflow-x-hidden box-border">
      <div className="flex w-full justify-between gap-4 pb-2 items-baseline">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-4 w-0.5 flex-none flex-col items-start bg-neutral-900" />
          <span className="font-['Inter'] text-[10px] font-[500] leading-[12px] text-neutral-600 uppercase tracking-widest">
            Monthly Trend
          </span>
        </div>
        {result.sectionSubtext ? (
          <span className="text-caption font-caption text-neutral-500 shrink-0 text-right">
            {result.sectionSubtext}
          </span>
        ) : null}
      </div>
      {result.error && (
        <div className="w-full rounded-lg bg-error-50 px-4 py-2" role="alert">
          <span className="text-caption font-caption text-error-600">
            {result.error}
          </span>
        </div>
      )}
      <RevenueVsExpensesCard
        {...(revenueVsExpenses as RevenueVsExpensesCardProps)}
      />
      <CashInOutCard {...cashInOut} />
    </div>
  );
}
